import { DT, TILE } from './constants';
import { isSolid, type Level } from './level';

/** A moving box. Position is top-left in world units; velocity in units/s. */
export interface Body {
  x: number;
  y: number;
  w: number;
  h: number;
  vx: number;
  vy: number;
}

export interface CollisionResult {
  onGround: boolean;
  hitCeiling: boolean;
  hitWall: boolean;
}

// Small inset so a box flush against a tile boundary doesn't grab the
// neighbouring cell when computing the tile span.
const EPS = 0.001;

/**
 * Integrate one tick of motion and resolve against solid tiles, axis-separated
 * (X first, then Y). Valid because per-tick displacement (≤ MAX_FALL_SPEED·DT =
 * 10 units) is always < TILE (16), so a single-cell check per axis cannot tunnel.
 * Mutates `body` in place and returns which surfaces it hit.
 */
export function moveBody(body: Body, level: Level): CollisionResult {
  const res: CollisionResult = { onGround: false, hitCeiling: false, hitWall: false };

  // ---- X axis ----
  body.x += body.vx * DT;
  {
    const y0 = Math.floor((body.y + EPS) / TILE);
    const y1 = Math.floor((body.y + body.h - EPS) / TILE);
    if (body.vx > 0) {
      const tx = Math.floor((body.x + body.w - EPS) / TILE);
      for (let ty = y0; ty <= y1; ty++) {
        if (isSolid(level, tx, ty)) {
          body.x = tx * TILE - body.w;
          body.vx = 0;
          res.hitWall = true;
          break;
        }
      }
    } else if (body.vx < 0) {
      const tx = Math.floor((body.x + EPS) / TILE);
      for (let ty = y0; ty <= y1; ty++) {
        if (isSolid(level, tx, ty)) {
          body.x = (tx + 1) * TILE;
          body.vx = 0;
          res.hitWall = true;
          break;
        }
      }
    }
  }

  // ---- Y axis ----
  body.y += body.vy * DT;
  {
    const x0 = Math.floor((body.x + EPS) / TILE);
    const x1 = Math.floor((body.x + body.w - EPS) / TILE);
    if (body.vy > 0) {
      const ty = Math.floor((body.y + body.h - EPS) / TILE);
      for (let tx = x0; tx <= x1; tx++) {
        if (isSolid(level, tx, ty)) {
          body.y = ty * TILE - body.h;
          body.vy = 0;
          res.onGround = true;
          break;
        }
      }
    } else if (body.vy < 0) {
      const ty = Math.floor((body.y + EPS) / TILE);
      for (let tx = x0; tx <= x1; tx++) {
        if (isSolid(level, tx, ty)) {
          body.y = (ty + 1) * TILE;
          body.vy = 0;
          res.hitCeiling = true;
          break;
        }
      }
    }
  }

  return res;
}
